import React, { Component } from 'react';
import { node, object, func } from 'prop-types';
import getObjectProperty from 'lodash/get';
import { compose, graphql, withApollo } from 'react-apollo';
import qs from 'query-string';

import * as AuthStates from './AuthStates';
import Auth0 from './configureAuth0';
import { Sentry } from '../utils';
import mixpanelAnalytics from '../analytics/MixpanelAnalytics';
import { getUserInfo } from '../api/Queries';
import { incrementVisitCount } from '../api/Mutations';
import moment from 'moment';

export const AuthContext = React.createContext();

export class AuthProvider extends Component {
  static propTypes = {
    children: node.isRequired,
    client: object.isRequired,
    incrementVisitCount: func.isRequired
  };

  state = {
    authSession: {},
    authState: AuthStates.Loading,
    authUser: {}
  };

  componentDidMount() {
    if (process.env.NODE_ENV !== 'test') {
      this.refreshAuthState();
    }
  }

  sessionFulfilled = async authSession => {
    const { client, incrementVisitCount } = this.props;
    let data;

    try {
      // fetch user data from our database
      const res = await client.query({
        query: getUserInfo,
        fetchPolicy: 'network-only'
      });

      data = res.data;
    } catch (e) {
      // fail silently
    }

    const authUser =
      authSession &&
      Object.assign(authSession.idTokenPayload, data && data.getUserInfo);

    // Because of the limitation of the OPENID protocol used by Auth0 (that AGI chose), any custom data such as roles/groups etc must be namespaced with a url.
    // Hence why we have to do to `https://aggrowth.com/roles` as the namespace. It can be anything, but for now that's the namespace i've set up on Auth0 dashboard for ease of use.
    const roles = getObjectProperty(
      authUser,
      `https://aggrowth.com/roles`,
      undefined
    );
    authUser.roles = roles;

    // check if user is authorized
    authUser.isAuthorized = !!(
      roles &&
      roles.length &&
      data &&
      data.getUserInfo
    );

    this.setState({
      authSession,
      authState: AuthStates.SignedIn,
      authUser
    });

    // Analytics Tracking - Login/First Access
    mixpanelAnalytics.trackLoginSuccessful();

    if (authUser.visitCount <= 0) {
      mixpanelAnalytics.trackEvent('First Access', {
        Timestamp: moment().format('YYYY/MM/DD*HH:mm:ss')
      });
    }

    incrementVisitCount();

    Sentry.configureScope(scope => {
      scope.setUser(authSession.idTokenPayload);
    });
  };

  // Handles session error and sets the state the logged out.
  sessionError = error => {
    // only print console error if the error is anything OTHER than a login required. Because auth0 returns an error for a login-required auth result from session check.
    if (error.code && error.code.toLowerCase() !== 'login_required') {
      console.error('Session error:', error); // eslint-disable-line no-console
    }

    this.setState({ authSession: {}, authState: AuthStates.SignedOut });
  };

  userFulfilled = userData => {
    this.setState({ authUser: userData });
    Sentry.configureScope(scope => {
      scope.setUser(userData);
    });
  };

  userFetchError = error => {
    console.error('Error fetching current user:', error); // eslint-disable-line no-console
    throw error;
  };

  // Refreshes the authstate.
  refreshAuthState = () => {
    // Check to see if user has a session.
    const isRedirectCallback =
      window.location.hash && window.location.hash.includes('#access_token');

    // check if user is blocked
    const parsedHash = qs.parse(window.location.hash);
    if (
      parsedHash.error === 'unauthorized' &&
      parsedHash.error_description === 'user is blocked'
    ) {
      this.setState({ authUser: { isBlocked: true } });
    }

    if (isRedirectCallback) {
      Auth0.parseHash({ hash: window.location.hash }, (err, authResult) => {
        if (err) {
          // not logged in and not a redirect from Auth0. Hash is random & not valid.
          this.sessionError(err);
        } else {
          // not logged in, BUT it's a redirect form Auth0 AND we have a token. Handle it.

          this.sessionFulfilled(authResult);
          window.history.replaceState({}, document.title, '/');
        }
      });
    } else {
      // Check to see if user has a session or not.
      Auth0.checkSession({}, (err, result) => {
        if (err) {
          // not logged in and not a redirect from Auth0.
          this.sessionError(err);
        } else {
          // User already logged-in and has a valid session.
          this.sessionFulfilled(result);
        }
      });
    }
  };

  //Fetches the user in session.
  fetchUser = accessToken => {
    Auth0.client.userInfo(accessToken, (err, user) => {
      if (err) {
        this.userFetchError(err);
      } else {
        this.userFulfilled(user);
      }
    });
  };

  // Fetches the id token from the current session
  fetchToken = () => this.authSession.idToken;

  // signIn the user using Auth0 Universal Login. Redirects to Auth0.
  signIn = () => {
    Auth0.authorize();
  };

  // signOut the user from Auth0
  signOut = () => {
    Auth0.logout({
      returnTo: window.location.origin
    });
  };

  render() {
    const { authSession, authState, authUser } = this.state;
    const { children } = this.props;
    return (
      <AuthContext.Provider
        value={{
          authSession,
          authState,
          authUser,
          // NOTE: addint actions here requires to add in proptype shape
          authActions: {
            signIn: this.signIn,
            signOut: this.signOut,
            getToken: this.fetchToken
          }
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }
}

const enhance = compose(
  graphql(incrementVisitCount, {
    props: props => ({
      incrementVisitCount: () => {
        return props.mutate().then(res => res);
      },
      ...props
    })
  }),
  withApollo
);
export default enhance(AuthProvider);
